import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Text,
    Box,
    Loader,
    Menu,
    Group,
    ActionIcon,
    Tooltip,
} from '@mantine/core';
import { Card, Button } from '@xplatform/design-system';
import {
    IconDownload,
    IconTable,
    IconBraces,
    IconChevronDown,
    IconArrowsMaximize,
    IconArrowsMinimize,
    IconLink,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useExtraction } from '@hooks/useContext';
import { SupportedDownloadFileType, type IExtractedNodeViewModel } from '@models/index';
import MemoizedHierarchicalNode from '@src/features/HierarchicalNode/HierarchicalNode';
import OpenMappingControl from '@src/features/MappingStatus/OpenMappingControl';
import RunMappingControl from '@src/features/MappingStatus/RunMappingControl';
import { buildReviewLink } from '@utils/reviewLink';
import './ExtractionResults.scss';

const collectExpandableIds = (nodes: IExtractedNodeViewModel[], ids: string[] = []): string[] => {
    for (const node of nodes) {
        if (node.children?.length) {
            ids.push(node.id);
            collectExpandableIds(node.children, ids);
        }
    }
    return ids;
};

const ExtractionResults: React.FC = () => {
    const { t } = useTranslation();
    const {
        selectNode,
        downloadResult,
        extractedResult,
        selectedNode,
        isLoadingResult,
        doctypeCode,
        selectedProcessResultInfo,
        selectedProcessItem,
        showSuccess,
        showError
    } = useExtraction();

    const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        if (extractedResult?.displayNode) {
            const topLevelIds = extractedResult.displayNode.map(node => node.id);
            setExpandedNodes(new Set(topLevelIds));
        }
    }, [extractedResult]);

    const toggleNode = useCallback((nodeId: string) => {
        setExpandedNodes(prev => {
            const newExpanded = new Set(prev);
            if (newExpanded.has(nodeId)) {
                newExpanded.delete(nodeId);
            } else {
                newExpanded.add(nodeId);
            }
            return newExpanded;
        });
    }, []);

    const expandableIds = useMemo(
        () => collectExpandableIds(extractedResult?.displayNode ?? []),
        [extractedResult],
    );
    const allExpanded = expandableIds.length > 0 && expandableIds.every(id => expandedNodes.has(id));

    const toggleExpandAll = useCallback(() => {
        setExpandedNodes(allExpanded ? new Set() : new Set(expandableIds));
    }, [allExpanded, expandableIds]);

    const copyToClipboard = useCallback((text?: string) => {
        if (text) {
            navigator.clipboard.writeText(text);
        }
    }, []);

    const canCopyReviewLink = Boolean(
        selectedProcessResultInfo && selectedProcessItem && extractedResult && !isLoadingResult
    );

    const copyReviewLink = useCallback(async () => {
        if (!selectedProcessResultInfo || !selectedProcessItem) return;
        try {
            await navigator.clipboard.writeText(
                buildReviewLink(selectedProcessResultInfo.id, selectedProcessItem.id)
            );
            showSuccess(t('extraction.extractedResults.reviewLinkCopied'));
        } catch {
            showError(t('extraction.extractedResults.reviewLinkCopyFailed'));
        }
    }, [selectedProcessResultInfo, selectedProcessItem, showSuccess, showError, t]);

    const handleDownload = async (fileType: SupportedDownloadFileType) => {
        if (!selectedProcessResultInfo || !selectedProcessItem || !doctypeCode) {
            return;
        }

        try {
            setIsDownloading(true);
            await downloadResult(selectedProcessResultInfo.id, selectedProcessItem.id, fileType);
        } catch (err) {
            console.error('Download failed:', err);
        } finally {
            setIsDownloading(false);
        }
    };

    const getFileTypeIcon = (fileType: SupportedDownloadFileType) => {
        switch (fileType) {
            case SupportedDownloadFileType.Excel:
                return <IconTable size={16} />;
            case SupportedDownloadFileType.Json:
                return <IconBraces size={16} />;
            default:
                return <IconDownload size={16} />;
        }
    };

    const getFileTypeLabel = (fileType: SupportedDownloadFileType) => {
        switch (fileType) {
            case SupportedDownloadFileType.Excel:
                return 'Excel (.xlsx)';
            case SupportedDownloadFileType.Json:
                return 'JSON (.json)';
            default:
                return fileType;
        }
    };

    const canDownload = selectedProcessResultInfo &&
        extractedResult &&
        !isLoadingResult &&
        doctypeCode;

    return (
        <Card className="extraction-results">
            <Group justify="space-between">
                <Group gap="xs" wrap="nowrap">
                    <Text size="lg" fw={600} component="h2">
                        Extracted Results
                    </Text>
                    {expandableIds.length > 0 && !isLoadingResult && (
                        <Button
                            size="xs"
                            variant="subtle"
                            leftSection={allExpanded
                                ? <IconArrowsMinimize size={14} />
                                : <IconArrowsMaximize size={14} />}
                            onClick={toggleExpandAll}
                            aria-expanded={allExpanded}
                            className="expand-all-button"
                        >
                            {t(allExpanded
                                ? 'extraction.extractedResults.collapseAll'
                                : 'extraction.extractedResults.expandAll')}
                        </Button>
                    )}
                </Group>

                <Group gap="sm" className="export-controls" wrap="nowrap">
                    {canCopyReviewLink && (
                        <Tooltip label={t('extraction.extractedResults.copyReviewLink')}>
                            <ActionIcon
                                variant="outline"
                                size="md"
                                onClick={copyReviewLink}
                                aria-label={t('extraction.extractedResults.copyReviewLink')}
                                className="copy-review-link-button"
                            >
                                <IconLink size={16} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                    <OpenMappingControl
                        processId={selectedProcessResultInfo?.id ?? ''}
                        enabled={Boolean(
                            selectedProcessResultInfo?.id &&
                            !isLoadingResult &&
                            selectedProcessResultInfo?.mappingEnabled
                        )}
                        showRetry={!selectedProcessResultInfo?.isBatch}
                    />
                    <RunMappingControl
                        processId={selectedProcessResultInfo?.id ?? ''}
                        doctypeCode={doctypeCode}
                        mappingAlreadyEnabled={Boolean(
                            selectedProcessResultInfo?.mappingEnabled
                        )}
                        showAction={!selectedProcessResultInfo?.isBatch}
                        enabled={Boolean(
                            selectedProcessResultInfo?.id &&
                            !isLoadingResult &&
                            canDownload
                        )}
                        onSuccess={showSuccess}
                        onError={showError}
                    />
                    <Menu position="bottom-end" portalProps={{ target: '#extraction-portal-root' }}>
                        <Menu.Target>
                            <Button
                                size="xs"
                                variant="outline"
                                leftSection={<IconDownload size={16} />}
                                rightSection={<IconChevronDown size={14} />}
                                disabled={!canDownload || isDownloading}
                                className="export-button"
                            >
                                {isDownloading ? 'Downloading...' : 'Export'}
                            </Button>
                        </Menu.Target>

                        <Menu.Dropdown>
                            {Object.values(SupportedDownloadFileType).map((fileType) => (
                                <Menu.Item
                                    key={fileType}
                                    leftSection={getFileTypeIcon(fileType)}
                                    onClick={() => handleDownload(fileType)}
                                    disabled={isDownloading}
                                >
                                    {getFileTypeLabel(fileType)}
                                </Menu.Item>
                            ))}
                        </Menu.Dropdown>
                    </Menu>
                </Group>
            </Group>
            <Box style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                {
                    isLoadingResult ?
                        <div className="spinner-container">
                            <Loader size={48} />
                        </div>
                        : <Box className="results-scrollable-container">
                            {extractedResult && (
                                <div>
                                    {extractedResult.displayNode.map((node) =>
                                        <MemoizedHierarchicalNode
                                            key={node.id}
                                            node={node}
                                            depth={0}
                                            isExpanded={expandedNodes.has(node.id)}
                                            isSelected={selectedNode?.id === node.id}
                                            expandedNodes={expandedNodes}
                                            selectedNode={selectedNode}
                                            onToggle={toggleNode}
                                            onSelect={selectNode}
                                            onCopy={copyToClipboard}
                                        />
                                    )}
                                </div>
                            )}
                        </Box>
                }
            </Box>
        </Card>
    );
};

export default ExtractionResults;
